import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ClipboardSignature, Download, FileText, Loader, Send, Upload, X } from 'lucide-react';
import { ListView, ListActionBar } from '../../common';
import { useHasAnyPermission } from '../../../hooks/usePermission';
import { OrdersService } from '../../../services/patientmr/orders.service';
import { POCCertificationsService } from '../../../services/pocCertifications.service';
import type { POCCertificationData } from '../../../services/pocCertifications.service';
import { ORDER_SENT_METHOD_LABELS, ORDER_TYPE_LABELS } from '../../../dtos/patientmr/orders.dto';
import type { OrderSentMethod, PhysicianOrder } from '../../../dtos/patientmr/orders.dto';
import type { PermissionString } from '../../../types/permissions';
import SignedCopyUpload from '../documents/SignedCopyUpload';
import AddOrderDialog from './AddOrderDialog';
import { CountersignBadge, UnsignedBadge, unsignedDeadline } from './orderDeadline';

interface OrdersViewProps {
  expanded: boolean;
  toggleExpanded: () => void;
  patientId?: string;
  readOnly?: boolean;
  /** Opens the POC / CMS-485 tab */
  onOpenPoc?: () => void;
}

const WRITE_PERMS = [
  'patientmr:phi:poc-certification:update',
  'patientmr:phi:poc-certification:updateall',
  'patientmr:phi:poc-certification:create',
  'patientmr:phi:poc-certification:createall',
] as PermissionString[];

const POC_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  pending_signature: 'Pending signature',
  signed: 'Signed',
  rejected: 'Rejected',
  expired: 'Expired',
};

const formatDate = (value?: string | null): string => {
  if (!value) return '';
  const [y, m, d] = value.slice(0, 10).split('-');
  return y && m && d ? `${m}/${d}/${y}` : value;
};

/** Unsigned badge for an order: server deadline when sent, else counted from creation */
export function orderDeadline(order: PhysicianOrder, today: Date = new Date()) {
  if (order.status === 'SIGNED') return null;
  return unsignedDeadline(order.createdAt, order.deadline ?? undefined, today);
}

/** The signed copy to download: the recorded signedDocumentId unless that document was removed */
export function signedOrderDocumentId(order: PhysicianOrder): string | null {
  if (!order.signedDocumentId) return null;
  const doc = (order.documents || []).find((d) => d.documentId === order.signedDocumentId);
  return doc?.deletedAt ? null : order.signedDocumentId;
}

const formatDateTime = (value?: string | null): string => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** Phase 19 F20 order filters */
export type OrderFilter = 'ALL' | 'TO_SEND' | 'AWAITING' | 'OVERDUE' | 'SIGNED';

export const ORDER_FILTER_LABELS: Record<OrderFilter, string> = {
  ALL: 'All',
  TO_SEND: 'To send',
  AWAITING: 'Awaiting signature',
  OVERDUE: 'Overdue',
  SIGNED: 'Signed',
};

/** Overdue: past the countersign due date, or (legacy orders) the red 14-day badge */
export function isOrderOverdue(order: PhysicianOrder, today: Date = new Date()): boolean {
  if (order.status === 'SIGNED') return false;
  if (order.countersign) return order.countersign.overdue;
  return orderDeadline(order, today)?.level === 'red';
}

export function orderMatchesFilter(order: PhysicianOrder, filter: OrderFilter, today: Date = new Date()): boolean {
  const unsigned = order.status !== 'SIGNED';
  switch (filter) {
    case 'TO_SEND': return unsigned && !order.sentAt;
    case 'AWAITING': return unsigned && !!order.sentAt;
    case 'OVERDUE': return isOrderOverdue(order, today);
    case 'SIGNED': return !unsigned;
    default: return true;
  }
}

const SENT_METHODS = Object.keys(ORDER_SENT_METHOD_LABELS) as OrderSentMethod[];

const pocDeadline = (cert: POCCertificationData, today: Date = new Date()) =>
  cert.status === 'draft' || cert.status === 'pending_signature'
    ? unsignedDeadline(cert.createdAt, cert.deadline, today)
    : null;

/** Phase 19 F20: structured / verbal order details (only the fields that are set) */
const OrderDetails: React.FC<{ order: PhysicianOrder }> = ({ order }) => {
  const rows: Array<[string, string]> = [];
  if (order.type === 'VERBAL' && (order.takenBy || order.takenAt)) {
    const by = order.takenBy
      ? [order.takenBy.name || 'Staff', order.takenBy.credentials].filter(Boolean).join(', ')
      : '';
    rows.push(['Taken by', [by, formatDateTime(order.takenAt)].filter(Boolean).join(' at ')]);
  }
  if (order.contactPerson) rows.push(['Given by', order.contactPerson]);
  if (order.type === 'VERBAL' && (order.detailVersion ?? 1) >= 2) rows.push(['Read back', order.readBack ? 'Yes' : 'No']);
  if (order.discipline) rows.push(['Discipline', order.discipline]);
  if (order.service) rows.push(['Service', order.service]);
  if (order.frequency) rows.push(['Frequency', order.frequency]);
  if (order.duration) rows.push(['Duration', order.duration]);
  if (order.effectiveFrom || order.effectiveTo) {
    rows.push(['Effective', `${formatDate(order.effectiveFrom) || 'open'} to ${formatDate(order.effectiveTo) || 'open'}`]);
  }
  if (rows.length === 0) return null;
  return (
    <dl className="mt-1 grid grid-cols-[auto,1fr] gap-x-2 text-xs text-slate-600" data-testid="order-details">
      {rows.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className="font-medium text-slate-700">{k}</dt>
          <dd className="break-words">{v}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
};

/** Orders view (ORD-09): plans of care and supplemental physician orders, with the 7/14-day unsigned badge. */
const OrdersView: React.FC<OrdersViewProps> = ({ expanded, toggleExpanded, patientId, readOnly = false, onOpenPoc }) => {
  const canWrite = useHasAnyPermission(WRITE_PERMS) && !readOnly;
  const [orders, setOrders] = useState<PhysicianOrder[]>([]);
  const [certs, setCerts] = useState<POCCertificationData[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [uploadFor, setUploadFor] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [pdfId, setPdfId] = useState<string | null>(null);
  const [filter, setFilter] = useState<OrderFilter>('ALL');
  const [sendMethods, setSendMethods] = useState<Record<string, OrderSentMethod>>({});
  const [sendingId, setSendingId] = useState<string | null>(null);
  const loadedFor = useRef<string | null>(null);

  const load = useCallback(async (mode: 'load' | 'refresh' = 'load') => {
    if (!patientId) return;
    if (mode === 'refresh') setRefreshing(true); else setLoading(true);
    setError(null);
    const [ordersResult, certsResult] = await Promise.allSettled([
      OrdersService.list(patientId),
      POCCertificationsService.listPOCCertifications(patientId),
    ]);
    if (ordersResult.status === 'fulfilled') {
      setOrders([...ordersResult.value].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0)));
    }
    if (certsResult.status === 'fulfilled') {
      setCerts(certsResult.value.certifications || []);
    }
    if (ordersResult.status === 'rejected' || certsResult.status === 'rejected') {
      setError('Some orders could not be loaded. Try refreshing.');
    }
    setLoading(false);
    setRefreshing(false);
  }, [patientId]);

  useEffect(() => {
    if (!expanded || !patientId) return;
    if (loadedFor.current === patientId) return;
    loadedFor.current = patientId;
    void load();
  }, [expanded, patientId, load]);

  const handleUpload = async (orderId: string, file: File, onProgress: (pct: number) => void) => {
    if (!patientId) return;
    const updated = await OrdersService.uploadSignedCopy(patientId, orderId, file, onProgress);
    if (updated) setOrders((prev) => prev.map((o) => (o.orderId === orderId ? { ...o, ...updated } : o)));
    setUploadFor(null);
    setNotice('Signed copy uploaded; the order is marked signed.');
    void load('refresh');
  };

  const handleDownload = async (order: PhysicianOrder) => {
    const documentId = signedOrderDocumentId(order);
    if (!patientId || !documentId) return;
    setDownloadingId(order.orderId);
    setError(null);
    try {
      const { url } = await OrdersService.getOrderDocumentUrl(patientId, order.orderId, documentId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Failed to download the signed copy');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleOrderPdf = async (order: PhysicianOrder) => {
    if (!patientId) return;
    setPdfId(order.orderId);
    setError(null);
    try {
      const { url } = await OrdersService.getOrderPdfUrl(patientId, order.orderId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Failed to prepare the order sheet');
    } finally {
      setPdfId(null);
    }
  };

  const handleMarkSent = async (order: PhysicianOrder) => {
    if (!patientId) return;
    const method = sendMethods[order.orderId] ?? 'FAX';
    setSendingId(order.orderId);
    setError(null);
    try {
      const updated = await OrdersService.markOrderSent(patientId, order.orderId, method);
      setOrders((prev) => prev.map((o) => (o.orderId === order.orderId
        ? { ...o, ...updated, sentMethod: updated.sentMethod ?? method, sentAt: updated.sentAt ?? new Date().toISOString() }
        : o)));
      setNotice(`Order marked as sent by ${ORDER_SENT_METHOD_LABELS[method].toLowerCase()}.`);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Failed to mark the order as sent');
    } finally {
      setSendingId(null);
    }
  };

  const visibleOrders = orders.filter((o) => orderMatchesFilter(o, filter));

  return (
    <>
      <ListView title="Orders" icon={<ClipboardSignature size={20} />} expanded={expanded} onToggleExpand={toggleExpanded}>
        <div className="space-y-4">
          <ListActionBar
            onRefresh={() => void load('refresh')}
            refreshing={refreshing}
            loading={loading}
            newButtonLabel="Add Order"
            onNewClick={() => setAddOpen(true)}
            showNew={canWrite && !!patientId}
          />
          {notice && (
            <div role="status" className="flex items-center justify-between gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
              <span>{notice}</span>
              <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss"><X size={14} /></button>
            </div>
          )}
          {error && (
            <div role="alert" className="flex items-center justify-between gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <span>{error}</span>
              <button type="button" onClick={() => setError(null)} aria-label="Dismiss error"><X size={14} /></button>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate-600">
              <Loader size={24} className="mr-2 animate-spin" /> Loading...
            </div>
          ) : (
            <>
              <section aria-labelledby="orders-poc-heading" className="space-y-2">
                <h3 id="orders-poc-heading" className="text-sm font-semibold text-slate-700 text-left">Plans of care (CMS-485)</h3>
                {certs.length === 0 ? (
                  <p className="text-sm text-slate-500 text-left">No plans of care yet.</p>
                ) : (
                  <ul className="space-y-2" data-testid="orders-poc-list">
                    {certs.map((cert) => (
                      <li key={String(cert.pocCertificationId || cert.id)} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 bg-white p-3">
                        <div className="min-w-0 text-left">
                          <div className="text-sm font-medium text-slate-800">
                            {formatDate(cert.certificationPeriodStart)} – {formatDate(cert.certificationPeriodEnd)}
                          </div>
                          <div className="text-xs text-slate-500">
                            {(cert.disciplines || [cert.discipline]).filter(Boolean).join(', ')}
                            {cert.physicianName ? ` · ${cert.physicianName}` : ''}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                            {POC_STATUS_LABELS[cert.status] ?? cert.status}
                          </span>
                          <UnsignedBadge deadline={pocDeadline(cert)} />
                          {onOpenPoc && (
                            <button type="button" onClick={onOpenPoc} className="min-h-[44px] px-1 text-xs font-medium text-blue-700 underline sm:min-h-0">Open</button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section aria-labelledby="orders-supplemental-heading" className="space-y-2">
                <h3 id="orders-supplemental-heading" className="text-sm font-semibold text-slate-700 text-left">Supplemental orders</h3>
                {orders.length > 0 && (
                  <div role="group" aria-label="Filter orders" className="flex flex-wrap gap-2" data-testid="order-filters">
                    {(Object.keys(ORDER_FILTER_LABELS) as OrderFilter[]).map((f) => (
                      <button
                        key={f}
                        type="button"
                        aria-pressed={filter === f}
                        onClick={() => setFilter(f)}
                        className={`min-h-[44px] rounded-full border px-3 py-1 text-xs font-medium sm:min-h-0 ${filter === f
                          ? 'border-slate-800 bg-slate-800 text-white'
                          : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`}
                      >
                        {ORDER_FILTER_LABELS[f]} ({orders.filter((o) => orderMatchesFilter(o, f)).length})
                      </button>
                    ))}
                  </div>
                )}
                {orders.length === 0 ? (
                  <p className="text-sm text-slate-500 text-left">No supplemental orders.</p>
                ) : visibleOrders.length === 0 ? (
                  <p className="text-sm text-slate-500 text-left">No orders match this filter.</p>
                ) : (
                  <ul className="space-y-2" data-testid="orders-list">
                    {visibleOrders.map((order) => (
                      <li key={order.orderId} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`order-${order.orderId}`}>
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0 flex-1 text-left">
                            <div className="text-sm font-medium text-slate-800">
                              {formatDate(order.date)} · {ORDER_TYPE_LABELS[order.type] ?? order.type}
                            </div>
                            <div className="text-sm text-slate-700 break-words">{order.summary}</div>
                            <div className="text-xs text-slate-500">
                              {order.orderingPhysician?.name}
                              {order.orderingPhysician?.npi ? ` (NPI ${order.orderingPhysician.npi})` : ''}
                            </div>
                            <OrderDetails order={order} />
                            {order.sentAt && (
                              <div className="text-xs text-slate-600" data-testid="order-sent">
                                Sent {formatDate(order.sentAt)}
                                {order.sentMethod ? ` by ${(ORDER_SENT_METHOD_LABELS[order.sentMethod] ?? order.sentMethod).toLowerCase()}` : ''}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {order.status === 'SIGNED' ? (
                              <span className="rounded border border-green-300 bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">Signed</span>
                            ) : order.countersign ? (
                              <CountersignBadge countersign={order.countersign} />
                            ) : (
                              <UnsignedBadge deadline={orderDeadline(order)} />
                            )}
                            <button
                              type="button"
                              onClick={() => void handleOrderPdf(order)}
                              disabled={pdfId === order.orderId}
                              className="inline-flex min-h-[44px] items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50 sm:min-h-0"
                              aria-label="Download order sheet"
                            >
                              <FileText size={12} aria-hidden="true" /> {pdfId === order.orderId ? 'Preparing...' : 'Order sheet'}
                            </button>
                            {signedOrderDocumentId(order) && (
                              <button
                                type="button"
                                onClick={() => void handleDownload(order)}
                                disabled={downloadingId === order.orderId}
                                className="inline-flex min-h-[44px] items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50 sm:min-h-0"
                                aria-label="Download signed copy"
                              >
                                <Download size={12} aria-hidden="true" /> {downloadingId === order.orderId ? 'Opening...' : 'Download'}
                              </button>
                            )}
                            {canWrite && order.status !== 'SIGNED' && (
                              <button
                                type="button"
                                onClick={() => setUploadFor(uploadFor === order.orderId ? null : order.orderId)}
                                className="inline-flex min-h-[44px] items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 sm:min-h-0"
                                aria-expanded={uploadFor === order.orderId}
                              >
                                <Upload size={12} aria-hidden="true" /> Signed copy
                              </button>
                            )}
                          </div>
                        </div>
                        {canWrite && order.status !== 'SIGNED' && !order.sentAt && (
                          <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2" data-testid="mark-sent">
                            <select
                              aria-label="Sent method"
                              value={sendMethods[order.orderId] ?? 'FAX'}
                              onChange={(e) => setSendMethods((prev) => ({ ...prev, [order.orderId]: e.target.value as OrderSentMethod }))}
                              className="min-h-[44px] rounded border border-slate-300 px-2 py-1 text-xs sm:min-h-0"
                            >
                              {SENT_METHODS.map((m) => <option key={m} value={m}>{ORDER_SENT_METHOD_LABELS[m]}</option>)}
                            </select>
                            <button
                              type="button"
                              onClick={() => void handleMarkSent(order)}
                              disabled={sendingId === order.orderId}
                              className="inline-flex min-h-[44px] items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50 sm:min-h-0"
                            >
                              <Send size={12} aria-hidden="true" /> {sendingId === order.orderId ? 'Saving...' : 'Mark as sent'}
                            </button>
                          </div>
                        )}
                        {uploadFor === order.orderId && (
                          <div className="mt-2 border-t border-slate-100 pt-2">
                            <SignedCopyUpload onUpload={(file, onProgress) => handleUpload(order.orderId, file, onProgress)} />
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>
      </ListView>

      {patientId && (
        <AddOrderDialog
          open={addOpen}
          patientId={patientId}
          onClose={() => setAddOpen(false)}
          onCreated={(order) => {
            setAddOpen(false);
            setOrders((prev) => [order, ...prev]);
            setNotice('Order added.');
          }}
        />
      )}
    </>
  );
};

export default OrdersView;
